import { useEffect, useMemo, useState, useCallback } from 'react';
import debounce from 'lodash/debounce'; // or implement your own debounce

/**
 * gridKey: e.g. 'bizpartners'
 * opts:
 *  - user: current user object (may contain user.datagrids[gridKey])
 *  - grids: account-level grids (grids[gridKey].cols etc)
 *  - gridHelpers: { renderCell, valueGetter, valueFormatter, getApplyQuickFilterFn }
 *  - query: URLSearchParams
 *  - persist: async function (gridKey, payload) => { ... } to save user overrides
 *  - defaultCols: optional default cols array (if grids missing)
 */
export function useGridConfig(gridKey, {
    user,
    grids,
    gridHelpers,
    query,
    persist,        // optional: function to persist user changes
    defaultCols = [],
}) {
    const [cols, setCols] = useState(null);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [usingFilter, setUsingFilter] = useState(false);
    const [filterName, setFilterName] = useState('');
    const [classLogic, setClassLogic] = useState(null);
    const [ready, setReady] = useState(false);

    // memoized maps of helper fns for fast lookup
    const helpers = useMemo(() => ({
        renderCell: gridHelpers?.renderCell || {},
        valueGetter: gridHelpers?.valueGetter || {},
        valueFormatter: gridHelpers?.valueFormatter || {},
        getApplyQuickFilterFn: gridHelpers?.getApplyQuickFilterFn || {},
    }), [ gridHelpers?.renderCell, gridHelpers?.valueGetter, gridHelpers?.valueFormatter, gridHelpers?.getApplyQuickFilterFn ]);

    // parse query params once
    const parsedQuery = useMemo(() => {
        const out = {};
        if (!query) return out;
        for (const [k,v] of query.entries()) out[k] = v;
        return out;
    }, [query]);

    // helper: safely parse JSON
    const safeParse = useCallback((s, fallback=null) => {
        try { return s ? JSON.parse(s) : fallback; }
        catch (e) { console.warn('parse error', e); return fallback; }
    }, []);

    // get storedCols source: preference order:
    // 1) if query.fn and grids[gridKey].filters[fn].cols -> that
    // 2) grids[gridKey].cols
    // 3) defaultCols
    const storedColsRaw = useMemo(() => {
        const g = grids && grids[gridKey];
        if (!g) return null;
        if (parsedQuery.fn && g.filters && g.filters[parsedQuery.fn] && g.filters[parsedQuery.fn].cols) {
            return safeParse(g.filters[parsedQuery.fn].cols, null);
        }
        return safeParse(g.cols, null) || defaultCols;
    }, [grids, gridKey, parsedQuery.fn, defaultCols, safeParse]);

    // resolve function names to actual functions (do not mutate original)
    const resolveRuntimeCols = useCallback((storedCols) => {
        if (!storedCols) return [];
        return storedCols.map(c => {
            const clone = { ...c };
            if (typeof clone.renderCell === 'string' && helpers.renderCell[clone.renderCell]) {
                clone.renderCell = helpers.renderCell[clone.renderCell];
            }
            if (typeof clone.valueGetter === 'string' && helpers.valueGetter[clone.valueGetter]) {
                clone.valueGetter = helpers.valueGetter[clone.valueGetter];
            }
            if (typeof clone.valueFormatter === 'string' && helpers.valueFormatter[clone.valueFormatter]) {
                clone.valueFormatter = helpers.valueFormatter[clone.valueFormatter];
            }
            if (typeof clone.getApplyQuickFilterFn === 'string' && helpers.getApplyQuickFilterFn[clone.getApplyQuickFilterFn]) {
                clone.getApplyQuickFilterFn = helpers.getApplyQuickFilterFn[clone.getApplyQuickFilterFn];
            }
            return clone;
        });
    }, [helpers]);

    // apply user overrides for width and order (optionally per-filter)
    const applyUserOverrides = useCallback((runtimeCols) => {
        if (!user || !user.datagrids || !user.datagrids[gridKey]) return runtimeCols.slice();

        const ud = user.datagrids[gridKey];

        // determine active filter in same order you use for storedCols
        const fn = parsedQuery.fn;
        const activeFilter = fn && ud.filters && ud.filters[fn];

        let colsCopy = runtimeCols.slice();

        // apply widths (colSize)
        const colSize = activeFilter?.colSize || ud.colSize || {};
        if (colSize && Object.keys(colSize).length) {
            colsCopy = colsCopy.map(c => ({ ...c, width: colSize[c.field] ?? c.width }));
        }

        // apply order
        const colOrder = activeFilter?.colOrder || ud.colOrder;
        if (Array.isArray(colOrder) && colOrder.length) {
            const inOrder = colOrder.map(field => colsCopy.find(c => c.field === field)).filter(Boolean);
            // preserve any columns not in colOrder at the end
            const missing = colsCopy.filter(c => !colOrder.includes(c.field));
            colsCopy = [...inOrder, ...missing];
        }

        return colsCopy;
    }, [user, gridKey, parsedQuery.fn]);

    // read filter criteria / className
    useEffect(() => {
        const g = grids && grids[gridKey];
        setUsingFilter(false);
        setFilterName('');
        setFilterModel({ items: [] });
        setClassLogic(null);

        if (!g) return;

        const fn = parsedQuery.fn;
        if (fn && g.filters && g.filters[fn]) {
            const f = g.filters[fn];
            setUsingFilter(true);
            setFilterName(fn);
            if (f.criteria) setFilterModel({ items: safeParse(f.criteria, []) });
            if (f.className) setClassLogic(f.className);
        } else {
            if (parsedQuery.crit) {
                setFilterModel({ items: safeParse(parsedQuery.crit, []) });
            } else if (g.className) {
                setClassLogic(g.className);
            }
        }
    // limited deps: don't depend on large objects unnecessarily
    }, [grids, gridKey, parsedQuery.fn, parsedQuery.crit, safeParse]);

    // Build and set cols when storedColsRaw, user, or helpers change
    useEffect(() => {
        if (!storedColsRaw) return;
        const runtimeCols = resolveRuntimeCols(storedColsRaw);
        const finalCols = applyUserOverrides(runtimeCols);
        setCols(finalCols);
        setReady(true);
    }, [storedColsRaw, resolveRuntimeCols, applyUserOverrides]);

    // ------- Persist handlers -------
    // saveUserLayout: debounced to limit API calls
    const saveUserLayout = useMemo(() => debounce((payload) => {
        if (typeof persist === 'function') {
            persist(gridKey, payload).catch(err => console.warn('persist failed', err));
        } else {
            // no-op or localStorage fallback
            try {
                localStorage.setItem(`datagrid.${gridKey}.user`, JSON.stringify(payload));
            } catch(e) {}
        }
    }, 500), [persist, gridKey]);

    // handler called by grid events
    const onColumnOrderChange = useCallback((newOrderArray, { api }) => {
        // newOrderArray: array of col field names in new order
        const payload = { colOrder: newOrderArray, updatedAt: new Date().toISOString() };
        saveUserLayout(payload);
    }, [saveUserLayout]);

    const onColumnResize = useCallback((colField, newWidth) => {
        // accumulate widths; better to pass entire colSize map
        const payload = { colSize: { [colField]: newWidth }, updatedAt: new Date().toISOString() };
        saveUserLayout(payload);
    }, [saveUserLayout]);

    const onFilterModelChange = useCallback((model) => {
        const payload = { filters: filterName ? { [filterName]: { criteria: JSON.stringify(model.items) } } : undefined, updatedAt: new Date().toISOString() };
        // for filters we may want to persist to account-level grid config rather than user
        saveUserLayout(payload);
        setFilterModel(model);
    }, [saveUserLayout, filterName]);

    // cleanup debounced function on unmount
    useEffect(() => () => saveUserLayout.cancel?.(), [saveUserLayout]);

    return {
        cols,
        filterModel,
        usingFilter,
        filterName,
        classLogic,
        ready,
        handlers: {
            onColumnOrderChange,
            onColumnResize,
            onFilterModelChange,
            saveUserLayoutImmediate: (p) => {
                // immediate save (no debounce)
                if (typeof persist === 'function') persist(gridKey, p);
                else localStorage.setItem(`datagrid.${gridKey}.user`, JSON.stringify(p));
            },
        },
    };
}